import React, { Component } from 'react';
import { Card, Row, Col, Tab, Tabs } from 'react-bootstrap';
import { Panel } from 'primereact/panel';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import CarteV2Service from '../../../services/CarteV2Service';
import PaymentReports from './Payment/PaymentReports';
import StockReports from './StockManagement/StockReports';
import TraceabilityReports from './Reports/TraceabilityReports';
import PrintedCardsReport from './PrintedCardsReport';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

class ComprehensiveReports extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'overview',
            loading: false,
            dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
            dateTo: new Date().toISOString().split('T')[0],

            // Overview statistics
            overviewStats: {
                totalCards: 0,
                activeCards: 0,
                cardsByStatus: {},
                cardsByCountry: {},
                productionRate: 0,
                deliveryRate: 0
            },

            // Production statistics
            productionStats: {
                byDay: [],
                byBatch: [],
                byTemplate: []
            },

            // Financial statistics
            financialStats: {
                totalRevenue: 0,
                totalCosts: 0,
                profitMargin: 0,
                revenueByMonth: []
            },

            // Quality statistics
            qualityStats: {
                totalQualityChecks: 0,
                passedChecks: 0,
                failedChecks: 0,
                defectRate: 0,
                defectsByType: []
            }
        };
    }

    componentDidMount() {
        this.loadOverviewData();
    }

    loadOverviewData = async () => {
        this.setState({ loading: true });

        try {
            // Load data from real APIs
            const [cards, batches, stats] = await Promise.all([
                CarteV2Service.searchCards({ status: null, page: 1, pageSize: 1000 }),
                CarteV2Service.getCardBatches(),
                CarteV2Service.getCardStatistics()
            ]);

            // Process cards data
            const cardsList = cards.data || cards || [];
            const totalCards = cardsList.length;
            const activeCards = cardsList.filter(c => c.status === 'Activated' || c.status === 'Delivered').length;

            // Group by status
            const cardsByStatus = cardsList.reduce((acc, card) => {
                const status = card.status || 'Unknown';
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});

            // Group by country
            const cardsByCountry = cardsList.reduce((acc, card) => {
                const country = card.countryCode || 'Unknown';
                acc[country] = (acc[country] || 0) + 1;
                return acc;
            }, {});

            // Calculate production rate (cards in production vs total)
            const inProduction = cardsList.filter(c =>
                ['InProduction', 'Printing', 'QualityControl', 'Packaging'].includes(c.status)
            ).length;
            const productionRate = totalCards > 0 ? ((inProduction / totalCards) * 100).toFixed(1) : 0;

            // Calculate delivery rate
            const delivered = cardsList.filter(c => c.status === 'Delivered').length;
            const deliveryRate = totalCards > 0 ? ((delivered / totalCards) * 100).toFixed(1) : 0;

            this.setState({
                overviewStats: {
                    totalCards,
                    activeCards,
                    cardsByStatus,
                    cardsByCountry,
                    productionRate: parseFloat(productionRate),
                    deliveryRate: parseFloat(deliveryRate)
                },
                loading: false
            });

        } catch (error) {
            console.error('Error loading overview data:', error);
            this.setState({ loading: false });
        }
    };

    renderOverviewTab = () => {
        const { overviewStats } = this.state;

        return (
            <div className="overview-tab">
                {/* KPI Cards */}
                <Row className="mb-4">
                    <Col lg={3} md={6} className="mb-3">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h6 className="text-muted mb-2">Total des Cartes</h6>
                                        <h3 className="mb-0">{overviewStats.totalCards.toLocaleString()}</h3>
                                    </div>
                                    <div className="ms-3">
                                        <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                            <i className="la la-id-card text-primary" style={{ fontSize: '2rem' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} md={6} className="mb-3">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h6 className="text-muted mb-2">Cartes Actives</h6>
                                        <h3 className="mb-0">{overviewStats.activeCards.toLocaleString()}</h3>
                                    </div>
                                    <div className="ms-3">
                                        <div className="rounded-circle bg-success bg-opacity-10 p-3">
                                            <i className="la la-check-circle text-success" style={{ fontSize: '2rem' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} md={6} className="mb-3">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h6 className="text-muted mb-2">Taux de Production</h6>
                                        <h3 className="mb-0">{overviewStats.productionRate}%</h3>
                                    </div>
                                    <div className="ms-3">
                                        <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                                            <i className="la la-industry text-warning" style={{ fontSize: '2rem' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} md={6} className="mb-3">
                        <Card className="border-0 shadow-sm">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <h6 className="text-muted mb-2">Taux de Livraison</h6>
                                        <h3 className="mb-0">{overviewStats.deliveryRate}%</h3>
                                    </div>
                                    <div className="ms-3">
                                        <div className="rounded-circle bg-info bg-opacity-10 p-3">
                                            <i className="la la-truck text-info" style={{ fontSize: '2rem' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Charts */}
                <Row>
                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="la la-chart-bar me-2"></i>
                                    Répartition par Statut
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Bar
                                    data={{
                                        labels: Object.keys(overviewStats.cardsByStatus),
                                        datasets: [{
                                            label: 'Nombre de cartes',
                                            data: Object.values(overviewStats.cardsByStatus),
                                            backgroundColor: [
                                                'rgba(54, 162, 235, 0.6)',
                                                'rgba(255, 99, 132, 0.6)',
                                                'rgba(255, 206, 86, 0.6)',
                                                'rgba(75, 192, 192, 0.6)',
                                                'rgba(153, 102, 255, 0.6)',
                                                'rgba(255, 159, 64, 0.6)'
                                            ]
                                        }]
                                    }}
                                    options={{
                                        responsive: true,
                                        plugins: {
                                            legend: { display: false },
                                            title: {
                                                display: false
                                            }
                                        }
                                    }}
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="la la-globe me-2"></i>
                                    Répartition par Pays
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut
                                    data={{
                                        labels: Object.keys(overviewStats.cardsByCountry),
                                        datasets: [{
                                            data: Object.values(overviewStats.cardsByCountry),
                                            backgroundColor: [
                                                'rgba(255, 99, 132, 0.6)',
                                                'rgba(54, 162, 235, 0.6)',
                                                'rgba(255, 206, 86, 0.6)',
                                                'rgba(75, 192, 192, 0.6)'
                                            ]
                                        }]
                                    }}
                                    options={{
                                        responsive: true,
                                        plugins: {
                                            legend: { position: 'bottom' }
                                        }
                                    }}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    render() {
        const { activeTab, loading } = this.state;

        return (
            <div className="comprehensive-reports">
                <div className="mb-4">
                    <h4>
                        <i className="la la-chart-line me-2"></i>
                        Rapports et Analytiques Complets
                    </h4>
                    <p className="text-muted">
                        Vue d'ensemble complète de la gestion des cartes d'assurance
                    </p>
                </div>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => this.setState({ activeTab: k })}
                    className="mb-4"
                >
                    <Tab eventKey="overview" title="Vue d'ensemble">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Chargement...</span>
                                </div>
                            </div>
                        ) : (
                            this.renderOverviewTab()
                        )}
                    </Tab>

                    <Tab eventKey="production" title="Production & Stock">
                        <Panel header="Rapports de Production et Stock">
                            <StockReports />
                        </Panel>
                    </Tab>

                    <Tab eventKey="traceability" title="Traçabilité">
                        <Panel header="Rapports de Traçabilité">
                            <TraceabilityReports />
                        </Panel>
                    </Tab>

                    <Tab eventKey="printed" title="Cartes Imprimées">
                        <Panel header="Rapport des Cartes Imprimées">
                            <PrintedCardsReport />
                        </Panel>
                    </Tab>

                    <Tab eventKey="payments" title="Paiements">
                        <Panel header="Rapports de Paiement">
                            <PaymentReports />
                        </Panel>
                    </Tab>
                </Tabs>
            </div>
        );
    }
}

export default ComprehensiveReports;
